import { ExternalMarketDataStrategyBase } from 'marketData/ExternalMarketDataStrategy';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import { Glue42Core } from '@glue42/core';
import {
    buildLogger,
    Logger,
    LoggingService,
} from '@instinet-analytics/core-lib';
import { v4 as uuid } from 'uuid';
import { BarData, TickData } from 'marketData/store/marketDataSlice';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { SymbolsService } from 'symbols/SymbolsService';

export interface Glue42InteropExternalMarketDataStrategyOptions {
    glueInteropApi: Glue42Core.Interop.API;
    loggingService: LoggingService;
    symbolsService: SymbolsService;
}

/*
 * If we are running in Glue, and the mktDataInterop query parameter is set,
 * we use this strategy for external market data,
 * which uses Glue42 Interop methods to communicate with the host
 */
export class Glue42InteropExternalMarketDataStrategy extends ExternalMarketDataStrategyBase {
    private readonly glueInteropApi: Glue42Core.Interop.API;
    private readonly logger: Logger;
    private symbolsService: SymbolsService;

    public constructor(
        options: Glue42InteropExternalMarketDataStrategyOptions
    ) {
        super();
        this.glueInteropApi = options.glueInteropApi;
        this.logger = buildLogger(
            'Glue42InteropExternalMarketDataStrategy',
            options.loggingService
        );
        this.logger.info(
            'Bootstrapping Glue42InteropExternalMarketDataStrategy'
        );
        this.symbolsService = options.symbolsService;

        (async () => await this.registerInteropMethods())();
    }

    public subscribe(request: MarketDataRequest): void {
        const payload = {
            correlationId: uuid(),
            ...request,
        };

        this.glueInteropApi.invoke(
            'Glue42InteropExternalMarketDataStrategy.subscribe',
            payload
        );
    }

    public unsubscribe(request: MarketDataRequest): void {
        const payload = {
            correlationId: uuid(),
            ...request,
        };

        this.glueInteropApi.invoke(
            'Glue42InteropExternalMarketDataStrategy.unsubscribe',
            payload
        );
    }

    private async registerInteropMethods() {
        await this.glueInteropApi.register(
            {
                name: 'Glue42InteropExternalMarketDataStrategy.receiveRefData',
                displayName:
                    'Glue42InteropExternalMarketDataStrategy.receiveRefData',
                description:
                    'Glue42InteropExternalMarketDataStrategy.receiveRefData',
            },
            (payload: {
                symbol: string;
                startTimestamp: number;
                endTimestamp: number;
                averageVolume: number;
                pnc: number;
            }) => {
                this.logger.info(
                    'Received Glue42InteropExternalMarketDataStrategy.receiveRefData',
                    payload
                );
                if (this.onRefDataReceivedHandler) {
                    this.onRefDataReceivedHandler({
                        bbSymbol: payload.symbol,
                        startTradingHoursTimestamp: payload.startTimestamp,
                        endTradingHoursTimestamp: payload.endTimestamp,
                        averageVolume: payload.averageVolume,
                        pnc: payload.pnc,
                    });
                }
            }
        );

        await this.glueInteropApi.register(
            {
                name: 'Glue42InteropExternalMarketDataStrategy.receiveMarketBarData',
                displayName:
                    'Glue42InteropExternalMarketDataStrategy.receiveMarketBarData',
                description:
                    'Glue42InteropExternalMarketDataStrategy.receiveMarketBarData',
            },
            (payload: { data: BarData[]; symbology: SymbologyValues }) => {
                this.logger.info(
                    'Received Glue42InteropExternalMarketDataStrategy.receiveMarketBarData',
                    payload
                );
                (async () => {
                    if (this.onMarketBarDataReceivedHandler) {
                        const mappedData: BarData[] = [];
                        for (const item of payload.data) {
                            const symbol =
                                (await this.symbolsService.convertSymbol(
                                    item.symbol,
                                    payload.symbology,
                                    SymbologyValues.Bloomberg
                                )) ?? item.symbol;
                            mappedData.push({ ...item, symbol });
                        }
                        this.onMarketBarDataReceivedHandler(mappedData);
                    }
                })();
            }
        );

        await this.glueInteropApi.register(
            {
                name: 'Glue42InteropExternalMarketDataStrategy.receiveMarketTickData',
                displayName:
                    'Glue42InteropExternalMarketDataStrategy.receiveMarketTickData',
                description:
                    'Glue42InteropExternalMarketDataStrategy.receiveMarketTickData',
            },
            (payload: { data: TickData[]; symbology: SymbologyValues }) => {
                this.logger.info(
                    'Received Glue42InteropExternalMarketDataStrategy.receiveMarketTickData',
                    payload
                );
                (async () => {
                    if (this.onMarketTickDataReceivedHandler) {
                        const mappedData: TickData[] = [];
                        for (const item of payload.data) {
                            const symbol =
                                (await this.symbolsService.convertSymbol(
                                    item.symbol,
                                    payload.symbology,
                                    SymbologyValues.Bloomberg
                                )) ?? item.symbol;
                            mappedData.push({ ...item, symbol });
                        }
                        this.onMarketTickDataReceivedHandler(mappedData);
                    }
                })();
            }
        );
    }
}
